import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { ListedIn } from 'types/back-office-settings';
import { RelationshipTypeCode } from 'types/contacts';
import { formatSubstitutePeriodSuffix } from '../util';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { MatterFormState } from './useMatterForm';

/** Our team: the supervising partner, the responsible lawyer, the case assistants and who handles the case. */
export const TeamFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const dateFormat = useEffectiveDateFormat();
    const {
        clientParty,
        isAdministrativeMatter,
        administratorFieldLabel,
        isAdministratorFieldEnabled,
        isAdministratorMandatoryForClients,
        isResponsibleLawyerNotMandatory,
        matter,
        prefillHandledBy,
        type,
    } = form;

    return (
        <>
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="matters.supervised-by"
                placeholder="matters.select-employee"
                name="supervisedByEmployee"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.CASE_SUPERVISED_BY } },
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                onChange={(val) => prefillHandledBy(val)}
                required={!isAdministrativeMatter}
            />
            {type === 'edit' && matter?.substituteSupervisingPartner ? (
                <InputWrapper
                    name="substituteSupervisingPartner"
                    label="matters.substitute-supervising-partner"
                    hasErrors={false}
                    readonly
                >
                    <ReadonlyField
                        value={matter.substituteSupervisingPartner.employee}
                        getValue={(option) => option?.id ?? null}
                        getLabel={(option) => option?.name ?? null}
                        linkTo="employee"
                        getSuffix={() =>
                            formatSubstitutePeriodSuffix(
                                matter.substituteSupervisingPartner!.startDate,
                                matter.substituteSupervisingPartner!.endDate,
                                dateFormat
                            )
                        }
                    />
                </InputWrapper>
            ) : null}
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="matters.responsible-lawyer"
                placeholder="matters.select-employee"
                name="responsibleEmployee"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                required={!isResponsibleLawyerNotMandatory}
                onChange={(val) => prefillHandledBy(val)}
            />
            {type === 'edit' && matter?.substituteResponsibleLawyer ? (
                <InputWrapper
                    name="substituteResponsibleLawyer"
                    label="matters.substitute-responsible-lawyer"
                    hasErrors={false}
                    readonly
                >
                    <ReadonlyField
                        value={matter.substituteResponsibleLawyer.employee}
                        getValue={(option) => option?.id ?? null}
                        getLabel={(option) => option?.name ?? null}
                        linkTo="employee"
                        getSuffix={() =>
                            formatSubstitutePeriodSuffix(
                                matter.substituteResponsibleLawyer!.startDate,
                                matter.substituteResponsibleLawyer!.endDate,
                                dateFormat
                            )
                        }
                    />
                </InputWrapper>
            ) : null}
            {isAdministratorFieldEnabled ? (
                <DynamicMultiSelectInput
                    setValue={setValue}
                    control={control}
                    label={administratorFieldLabel}
                    shouldTranslateLabel={false}
                    placeholder="matters.select-employee"
                    name="administrators"
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        additionalParams: { extraParams: { listedIn: ListedIn.CASE_ASSISTANT } },
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    onChange={(_value, reason, details) => {
                        if (reason === 'selectOption') prefillHandledBy(details?.option ?? null);
                    }}
                    required={
                        isAdministratorMandatoryForClients &&
                        clientParty?.relationshipTypeCode === RelationshipTypeCode.CLIENT
                    }
                />
            ) : null}
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="matters.handled-by"
                placeholder="matters.select-employee"
                name="employees"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
        </>
    );
};
